<template>
  <div class="home-echarts-wrap" :style="{ width, height, marginBottom }">
    <div class="home-echarts-header" style="padding: 10px 24px 0 24px;height: auto">
      <h3 class="home-echarts-header__title">{{ title }}</h3>
      <div class="home-echarts-header__controls">
        <a-date-picker
            @change="handleDateChange"
            v-model:value="yearValue"
            :allowClear="false"
            picker="year"
        />
      </div>
    </div>
    <!-- 延迟显示 loading 效果。当 spinning 状态在 delay 时间内结束，则不显示 loading 状态。 -->
    <a-spin :spinning="spinning" :delay="200">
      <!-- echarts 图表 -->
      <div ref="chartRef" class="home-echarts-content" v-show="showECharts"></div>
      <dc-layout-empty type="nothing" v-if="!showECharts"/>
    </a-spin>
  </div>
</template>

<script setup lang="ts">
import type { PropType, Ref } from 'vue'
import { onBeforeMount, reactive, ref, watch } from 'vue'
import { useECharts } from '@/hooks/useECharts'
import dayjs, { Dayjs } from 'dayjs'
import { getMonthlyTargetCompletion } from '@/api/gucangSales/dataBoard'

interface QueryFormType {
  employeeNo?: string
  dataPermissionQueryType: number
  startDay?: string
  endDay?: string
}

interface ItemCommonType {
  monthValue?: number,
  targetValueTotal?: number, // 月度业绩目标值
  completionValueTotal?: number, // 月度业绩实际完成值
  targetCompletionRate?: string, // 仅显示
  targetCompletionRateNum?: number, // 供图表数据
}
const yearValue = ref<Dayjs>(dayjs())
const props = defineProps({
  title: {
    type: String as PropType<string>,
    default: '每月业绩目标完成情况'
  },
  appId: {
    type: String as PropType<'cos_fbu' | 'cos_lbu'>,
    required: true
  },
  width: {
    type: String as PropType<string>,
    default: '100%'
  },
  height: {
    type: String as PropType<string>,
    default: '560px'
  },
  marginBottom: {
    type: String as PropType<string>,
    default: '8px'
  },
  allowClear: {
    type: Boolean as PropType<boolean>,
    default: false
  },
  employeeNo: {
    type: String as PropType<string>
  }
})

const spinning = ref(false)
const showECharts = ref(false)
const startMonth = ref<string>(dayjs(yearValue.value).startOf('month').format('YYYY01'))
const endMonth = ref<string>(dayjs(yearValue.value).endOf('month').format('YYYY12'))
const xAxisData = ref<any[]>([])
const queryForm = reactive<QueryFormType>({
  startDay: dayjs(yearValue.value).startOf('year').format('YYYY/MM/DD'),
  endDay: dayjs(yearValue.value).endOf('year').format('YYYY/MM/DD'),
  dataPermissionQueryType: props.employeeNo ? 2 : 1,
  employeeNo: props.employeeNo
})
watch(() => props.employeeNo, (val) => {
  queryForm.employeeNo = val
  if (val) {
    queryForm.dataPermissionQueryType = 2
  } else {
    queryForm.dataPermissionQueryType = 1
  }
  getData()
})
const handleDateChange = () => {
  queryForm.startDay = dayjs(yearValue.value).startOf('year').format('YYYY/MM/DD')
  queryForm.endDay = dayjs(yearValue.value).endOf('year').format('YYYY/MM/DD')
  startMonth.value = dayjs(yearValue.value).startOf('month').format('YYYY01')
  endMonth.value = dayjs(yearValue.value).endOf('month').format('YYYY12')
  xAxisData.value = generateTimeArray(startMonth.value, endMonth.value, 'YYYYmm')
  getData()
}

function generateTimeArray(startTime: string, endTime: string, format: 'YYYYmm' | 'YYYYww' | 'YYYY/MM/DD') {
  const start = dayjs(startTime, format)
  const end = dayjs(endTime, format)
  const result = []

  let current = start

  if (format === 'YYYYmm') {
    while (current.isBefore(end) || current.isSame(end)) {
      result.push({
        value: current.format(format),
        name: current.format('m') + '月'
      })
      current = current.add(1, 'm')
    }
  } else if (format === 'YYYY/MM/DD') {
    while (current.isBefore(end) || current.isSame(end)) {
      result.push({
        value: current.format(format),
        name: current.format('D')
      })
      current = current.add(1, 'day')
    }
  } else {
    throw new Error('Unsupported format')
  }
  return result
}

const getData = () => {
  spinning.value = true
  getMonthlyTargetCompletion(queryForm).then((res) => {
    const targetCompletionList = res.data?.targetCompletionList as unknown as ItemCommonType[]
    const transTargetCompletionList = transChartData(targetCompletionList)
    if (transTargetCompletionList.length) {
      showECharts.value = true
      let maxTarget = 0
      let maxRate = 100
      transTargetCompletionList.forEach(value => {
        if (value) {
          const targetValue = value.targetValueTotal ?? 0
          const completionRate = value.targetCompletionRateNum ?? 0
          const targetCompletion = value.completionValueTotal ?? 0
          if (targetValue > maxTarget) {
            maxTarget = targetValue
          }
          if (targetCompletion > maxTarget) {
            maxTarget = targetCompletion
          }
          if (completionRate > maxRate) {
            maxRate = completionRate
          }
        }
      })
      initChart(transTargetCompletionList, maxTarget, Math.ceil(maxRate))
    } else {
      showECharts.value = false
      initChart([], 0)
    }
  }).finally(() => {
    spinning.value = false
  })
}
/// 格式化接口数据和日期对应
const transChartData = (serviceData: ItemCommonType[]) => {
  const matchData: ItemCommonType[] = []
  if (serviceData && serviceData.length > 0) {
    xAxisData.value.forEach((axis) => {
      const matchItem = serviceData.find((item) => {
        return (item.monthValue ?? 0).toString() === axis.value
      })
      if (matchItem) {
        matchItem.targetCompletionRateNum = matchItem.targetValueTotal ? Number((((matchItem.completionValueTotal ?? 0) / matchItem.targetValueTotal) * 100).toFixed(2)) : 0
        matchData.push(matchItem)
      } else {
        matchData.push(undefined as unknown as ItemCommonType)
      }
    })
  }
  return matchData
}
const chartRef = ref<HTMLDivElement | null>(null)
const { setOptions } = useECharts(chartRef as Ref<HTMLDivElement>, undefined, false)
const initChart = (transTargetCompletionList: ItemCommonType[], maxTarget: number, maxRate = 100) => {
  setOptions({
    // 悬浮窗
    tooltip: {
      trigger: 'axis',
      backgroundColor: '#1D4CD2',
      textStyle: {
        color: 'White'
      },
      formatter: function (params) {
        const currentData = xAxisData.value[params[0].dataIndex].value
        const toolsTipsResult = []
        toolsTipsResult.push(currentData)
        for (let i = 0; i < params.length; i++) {
          const currentParam = params[i]
          toolsTipsResult.push(currentParam.seriesName + '  <strong>' + (i === 2 ? currentParam.value + '%' : currentParam.value) + '</strong>')
          // toolsTipsResult.push('目标值  <strong>' + currentHoverData?.quantity + '</strong>')
          // toolsTipsResult.push('完成值  <strong>' + currentHoverData?.total + '</strong>')
          // toolsTipsResult.push(`完成率  <strong>${(currentHoverData?.total ? Number((((currentHoverData.quantity ?? 0) / currentHoverData.total) * 100).toFixed(2)) : 0)}%` + '</strong>')
        }
        return toolsTipsResult.join('<br>')
      }
    },
    /// 在这里配置线条颜色和legend颜色一致
    color: ['#BCC7DD', '#1D4CD2', '#FF9A3E'],
    legend: {
      itemWidth: 12,
      itemHeight: 2,
      left: 'right',
      icon: 'react',
      right: '50%',
      data: ['目标值', '完成值', '完成率']
    },
    grid: {
      left: '3%',
      right: '4%',
      bottom: '3%',
      containLabel: true
    },
    xAxis: {
      type: 'category',
      boundaryGap: false,
      data: xAxisData.value.map((item) => item.name)
    },
    yAxis: [
      {
        type: 'value',
        axisLabel: {
          formatter: '{value} %'
        },
        position: 'right',
        max: maxRate,
        splitLine: {
          show: true,
          lineStyle: {
            color: '#D8DFE4',
            type: 'dashed'
          }
        }
      },
      {
        type: 'value',
        position: 'left',
        max: maxTarget ? Math.ceil(maxTarget * 1.1) : 50,
        // axisLabel: {
        //   show: false // 设置为false以隐藏Y轴刻度
        // },
        splitLine: {
          show: false
        }
      }
    ],
    series: [
      {
        name: '目标值',
        type: 'bar',
        barWidth: '40%',
        yAxisIndex: 1,
        data: transTargetCompletionList.map(value => value?.targetValueTotal ?? 0)
      },
      {
        name: '完成值',
        barWidth: '20%',
        type: 'bar',
        barGap: '-75%',
        yAxisIndex: 1,
        data: transTargetCompletionList.map(value => value?.completionValueTotal ?? 0)
      },
      {
        name: '完成率',
        type: 'line',
        smooth: true,
        yAxisIndex: 0,
        data: transTargetCompletionList.map(value => value?.targetCompletionRateNum ?? 0)
      }
    ]
  })
}
onBeforeMount(() => {
  if (!queryForm.startDay) {
    queryForm.startDay = dayjs(yearValue.value).startOf('year').format('YYYY/MM/DD')
  }
  if (!queryForm.endDay) {
    queryForm.endDay = dayjs(yearValue.value).endOf('year').format('YYYY/MM/DD')
  }
  xAxisData.value = generateTimeArray(startMonth.value, endMonth.value, 'YYYYmm')
  getData()
})
</script>
<script lang="ts">
export default {
  name: 'MonthlyTargetCompletion'
}
</script>
<style scoped lang="scss">

</style>
